/**
 * Responsibility: the question - the phrase, how to rank it, and go.
 *
 * Submitted rather than live. Each result costs a page read from a free
 * catalogue, so searching on every keystroke would be both slow and rude;
 * the box holds a draft and the query only leaves on Enter or the button.
 *
 * **One row, and only the controls that decide what the results are.** The
 * mode is here because it changes what the list *means* rather than which
 * of it is shown - filters narrow a list, the mode decides what the list is
 * ranked by. Everything that merely narrows went behind the filter button,
 * which is what bought the page back the room to show its results.
 *
 * The mode's explanation used to sit under the box on its own line, which
 * cost a row of the window to say something the results already say - the
 * Found figure carries it as its helper. It is a tooltip here.
 */
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import BookmarkIcon from '@mui/icons-material/BookmarkAddOutlined';
import ScanIcon from '@mui/icons-material/QrCodeScannerOutlined';
import { useState } from 'react';
import type { MarketCategory, MarketMode, SavedSearch } from '../../api/client';
import { FADE_MS, useRotatingHint } from '../../hooks/useRotatingHint';
import { ScanDialog } from '../scan/ScanDialog';
import type { SearchDraft } from './searchDraft';

/** Cycled through the placeholder. Chosen to teach the two things that
 *  change the results: the name as printed, and "autograph" not "signed". */
const HINTS = [
  'Ohtani rookie topps',
  'vintage mew pokemon',
  'stephen curry autograph rookie',
];

const MODE_HINTS: Record<MarketMode, string> = {
  BEST_MATCH: 'The card you typed, first. Use this when you know what you are looking for.',
  ARBITRAGE: 'Where the gap between a raw copy and a graded one is worth crossing.',
  TOP_VALUE: 'What this name is worth at its best: which printing, which grade.',
};

/** Wide enough to read a card name back, capped so it stops somewhere
 *  sensible rather than running the width of a large monitor. */
const FIELD_MIN = 200;
const FIELD_MAX = 520;

type SearchBarProps = {
  draft: SearchDraft;
  onChange: (next: SearchDraft) => void;
  onSubmit: (draft: SearchDraft) => void;
  busy: boolean;
  /** Searches already kept, offered as completions while typing. */
  suggestions?: SavedSearch[];
  /** Absent until a search has been run: there is nothing to save before. */
  onSave?: () => void;
  saving?: boolean;
};

export function SearchBar({
  draft,
  onChange,
  onSubmit,
  busy,
  suggestions = [],
  onSave,
  saving,
}: SearchBarProps) {
  const [focused, setFocused] = useState(false);
  const [scanning, setScanning] = useState(false);
  // Stops once there is anything to compare against, and while the field
  // has focus: a placeholder moving under someone mid-thought is noise.
  const { hint, visible } = useRotatingHint(HINTS, focused || draft.q.length > 0);

  const submit = (next: SearchDraft = draft) => {
    if (next.q.trim().length < 2) return;
    onSubmit(next);
  };

  /**
   * A scanned code becomes the query and runs immediately.
   *
   * Straight to a search rather than into the box to be confirmed: the
   * phone is being held over a card, and a step that needs a second tap on
   * the same screen is a step nobody wanted.
   */
  const scanned = (rawValue: string) => {
    setScanning(false);
    const value = rawValue.trim();
    if (value.length < 2) return;
    const next = { ...draft, q: value };
    onChange(next);
    submit(next);
  };

  return (
    <>
      {/* Fills the row, and starts at the left of it. Justifying this to
          the end pushed the box over against the buttons, which read as
          one crowded clump on the right with dead space beside it. */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ minWidth: 0, flexGrow: 1 }}
      >
        <Autocomplete
          freeSolo
          // Free text is the normal case; the saved list is a shortcut on
          // top of it, never a set of choices to pick from.
          options={suggestions}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
          filterOptions={(options, state) => {
            const typed = state.inputValue.trim().toLowerCase();
            if (typed.length < 2) return [];
            return options.filter(
              (option) =>
                option.label.toLowerCase().includes(typed) ||
                option.query.toLowerCase().includes(typed),
            );
          }}
          inputValue={draft.q}
          onInputChange={(_event, next) => onChange({ ...draft, q: next })}
          onChange={(_event, chosen) => {
            // A saved search carries its filters. Picking one runs the
            // whole thing, which is the only reason it was worth saving.
            if (chosen === null || typeof chosen === 'string') return;
            const next: SearchDraft = {
              ...draft,
              q: chosen.query,
              mode: chosen.mode as MarketMode,
              category: (chosen.category ?? '') as MarketCategory | '',
              maxRaw: chosen.max_raw ?? '',
              minValue: chosen.min_value ?? '',
            };
            onChange(next);
            submit(next);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <BookmarkIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                <Typography variant="body2" noWrap>
                  {option.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {option.mode === 'ARBITRAGE' ? 'arbitrage' : 'top value'}
                  {option.result_count === null ? '' : ` - ${option.result_count} then`}
                </Typography>
              </Stack>
            </li>
          )}
          // The box takes the spare width - a wider box is a better box,
          // where a wider button is not - and `mr: auto` collects whatever
          // is left after the cap, so the box sits on the left and every
          // button ends up together on the right rather than crowded
          // against it.
          sx={{ flexGrow: 1, minWidth: FIELD_MIN, maxWidth: FIELD_MAX, mr: 'auto' }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={hint}
              aria-label="Search any card"
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              onKeyDown={(event) => event.key === 'Enter' && submit()}
              sx={{
                '& input::placeholder': {
                  opacity: visible ? 0.6 : 0,
                  transition: `opacity ${FADE_MS}ms`,
                },
              }}
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />

        <Tooltip title={MODE_HINTS[draft.mode]}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={draft.mode}
            onChange={(_event, next: MarketMode | null) =>
              next && onChange({ ...draft, mode: next })
            }
            sx={{ '& .MuiToggleButton-root': { whiteSpace: 'nowrap' } }}
          >
            <ToggleButton value="BEST_MATCH">Best match</ToggleButton>
            <ToggleButton value="TOP_VALUE">Top value</ToggleButton>
            <ToggleButton value="ARBITRAGE">Arbitrage</ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>

        <Tooltip title="Scan a barcode">
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setScanning(true)}
              disabled={busy}
              sx={{ minWidth: 0, px: 1.25 }}
              aria-label="Scan a barcode"
            >
              <ScanIcon fontSize="small" />
            </Button>
          </span>
        </Tooltip>

        <Button
          size="small"
          variant="contained"
          onClick={() => submit()}
          disabled={busy || draft.q.trim().length < 2}
          // Nothing shrinks the label. In a row with a field and the mode
          // switch, flex squeezed this narrow enough to break "Search"
          // across two lines.
          sx={{ flexShrink: 0, whiteSpace: 'nowrap', px: 2.5 }}
        >
          Search
        </Button>

        {onSave ? (
          <Tooltip title="Keep this search">
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={onSave}
                disabled={saving || busy}
                sx={{ minWidth: 0, px: 1.25 }}
                aria-label="Keep this search"
              >
                <BookmarkIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        ) : null}
      </Stack>

      <ScanDialog
        open={scanning}
        title="Scan a card"
        hint="Point the camera at a barcode. Sealed product carries a UPC; a graded slab carries its certificate number."
        onClose={() => setScanning(false)}
        onScanned={(code) => scanned(code.rawValue)}
      />
    </>
  );
}
